import { Code, Loader2 } from "lucide-react"
import { observer } from "mobx-react"
import { type ReactNode, useEffect, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { setLastViewed, setWorkspaceLastViewed } from "../constants"
import { initCodeModuleCapabilities } from "../nestAPIs/capabilities"
import { fetchPlatformInfo } from "../nestAPIs/platform"
import { DesktopRequiredPage } from "../pages/DesktopRequiredPage"
import { currentPlatformKind } from "../platform/detect"
import { useCodeNavigate } from "../routing"
import { useCodeStore } from "../store/context"
import { CodeAppLayout } from "./CodeAppLayout"
import { useRouteOverlayActive } from "./RouteOverlay"
export interface CodeLayoutProps {
    children: ReactNode
    isCodeModuleAvailable: boolean
    workspaceId?: string
    taskId?: string
    title: string | ReactNode
    icon: ReactNode
    navbarCenter?: ReactNode
    navbarBelow?: ReactNode
    navbarRight?: ReactNode
    navbarOverlay?: boolean
    navbarVisible?: boolean
}

export const CodeLayout = observer(
    ({
        children,
        isCodeModuleAvailable,
        workspaceId,
        taskId,
        title,
        icon,
        navbarCenter,
        navbarBelow,
        navbarRight,
        navbarOverlay,
        navbarVisible,
    }: CodeLayoutProps) => {
        const codeStore = useCodeStore()
        const navigate = useCodeNavigate()
        const routeOverlayActive = useRouteOverlayActive()
        const browserNewTaskShortcut = currentPlatformKind() === "browser"
        const [hasInitialized, setHasInitialized] = useState(false)

        useHotkeys(
            "meta+n,ctrl+n",
            (event) => {
                if (!workspaceId) return
                event.preventDefault()
                if (document.querySelector("[role='dialog'][aria-modal='true']")) return
                navigate.openTaskCreate(workspaceId)
            },
            {
                enabled: Boolean(workspaceId) && !routeOverlayActive,
                enableOnContentEditable: true,
                enableOnFormTags: ["INPUT", "TEXTAREA", "SELECT"],
            },
            [navigate, routeOverlayActive, workspaceId]
        )

        useHotkeys(
            "n",
            (event) => {
                if (!workspaceId) return
                event.preventDefault()
                if (document.querySelector("[role='dialog'][aria-modal='true']")) return
                navigate.openTaskCreate(workspaceId)
            },
            {
                enabled: browserNewTaskShortcut && Boolean(workspaceId) && !routeOverlayActive,
                enableOnContentEditable: false,
                enableOnFormTags: false,
            },
            [browserNewTaskShortcut, navigate, routeOverlayActive, workspaceId]
        )

        // Gate access to desktop app with code modules only
        if (!isCodeModuleAvailable) {
            return (
                <CodeAppLayout
                    navbar={{
                        title: "Code",
                        icon: <Code size="1.25rem" className="text-muted" />,
                        right: null,
                    }}
                >
                    <DesktopRequiredPage />
                </CodeAppLayout>
            )
        }

        // Load repos on mount and initialize capabilities + platform info
        useEffect(() => {
            const init = async () => {
                // Initialize code module capabilities and platform info early (caches for subsequent calls)
                await initCodeModuleCapabilities()
                await fetchPlatformInfo()
                await codeStore.repos.loadRepos()
                setHasInitialized(true)
            }
            init()
        }, [])

        // Ensure tasks are loaded when workspace changes
        useEffect(() => {
            if (workspaceId) {
                codeStore.tasks.ensureTasksLoaded(workspaceId)
            }
        }, [workspaceId])

        useEffect(() => {
            const refreshRunningTasks = () => {
                void codeStore.refreshRuntimeStateFromNest()
            }
            window.addEventListener("focus", refreshRunningTasks)
            return () => window.removeEventListener("focus", refreshRunningTasks)
        }, [codeStore])

        // Check if tasks are loaded for the current workspace
        const tasksLoaded = !workspaceId || codeStore.tasks.loadedRepoIds.has(workspaceId)

        // Track last viewed workspace/task for sidebar navigation
        useEffect(() => {
            if (workspaceId) {
                setLastViewed({ workspaceId, taskId })
                setWorkspaceLastViewed(workspaceId, { taskId })
            }
        }, [workspaceId, taskId])

        // Show loading state
        if (!hasInitialized || codeStore.repos.reposLoading || !tasksLoaded) {
            return (
                <CodeAppLayout
                    navbar={{
                        title: "Code",
                        icon: <Code size="1.25rem" className="text-muted" />,
                        right: null,
                    }}
                >
                    <div className="flex flex-col items-center justify-center h-full text-muted">
                        <Loader2 size="2rem" className="animate-spin mb-4 opacity-50" />
                        <div className="text-sm">Loading...</div>
                    </div>
                </CodeAppLayout>
            )
        }

        return (
            <CodeAppLayout
                navbar={{
                    title,
                    icon,
                    center: navbarCenter,
                    below: navbarBelow,
                    right: navbarRight,
                    overlay: navbarOverlay,
                    visible: navbarVisible,
                }}
            >
                {children}
            </CodeAppLayout>
        )
    }
)
